import { useState, useEffect } from "react";
import { useGlobalStore } from "@/app/context/globalStoreProvider";
import { ActiveStatusChallengeResponse } from "@/generated/email_parser.openapi";

/**
 * Hook to fetch active status challenge details from the API.
 * Returns null if user has no active challenge or if fetching fails.
 */
export function useActiveStatusChallenge() {
  const { user } = useGlobalStore();
  const [challenge, setChallenge] =
    useState<ActiveStatusChallengeResponse | null>(null);
  // Initialize loading to true when user has an active challenge so consumers
  // don't render progress UI with fallback defaults before the fetch resolves.
  const [loading, setLoading] = useState(
    () => !!user?.data?.activeStatusChallenge
  );
  // True once a fetch attempt has resolved (success, 404, or error). Lets
  // consumers distinguish "haven't fetched yet" from "fetched and got nothing"
  // so they don't gate on a spinner forever after a failed/empty GET.
  const [settled, setSettled] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchChallenge() {
      // Don't fetch if user doesn't have an active challenge. Clear loading too
      // (it may have been initialized true) so consumers don't stay gated after
      // a challenge ends.
      if (!user?.data?.activeStatusChallenge) {
        setChallenge(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await fetch("/api/user/status_challenge", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.status === 404) {
          // No active challenge
          setChallenge(null);
        } else if (response.ok) {
          const data = await response.json();
          const challengeData: ActiveStatusChallengeResponse = {
            ...data,
            startDate: new Date(data.startDate),
            endDate: new Date(data.endDate),
          };
          setChallenge(challengeData);
        } else {
          throw new Error(
            `Failed to fetch status challenge: ${response.statusText}`
          );
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error";
        setError(errorMessage);
        setChallenge(null);
      } finally {
        setLoading(false);
        setSettled(true);
      }
    }

    fetchChallenge();
  }, [user?.data?.activeStatusChallenge]);

  return { challenge, loading, settled, error };
}
